import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScopeExchangeStore } from "../store/scopeExchangeStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Scope Exchange (v3)
 * 
 * UI for managing scope exchange payloads, integrity scores,
 * latency, and reconciliation delays between counterparties.
 */
export default function ScopeExchange() {
  const { t } = useTranslation();
  const {
    outgoingPayloadKB,
    incomingPayloadKB,
    scopeCategory,
    exchangeCounterpart,
    integrityScore,
    reconciliationDelayDays,
    downstreamPropCoeff,
    setOutgoingPayloadKB,
    setIncomingPayloadKB,
    setScopeCategory,
    setExchangeCounterpart,
    setIntegrityScore,
    setReconciliationDelayDays,
    setDownstreamPropCoeff,
    exportScopeExchange,
  } = useScopeExchangeStore();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("ScopeExchangePanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("outgoingPayloadKB", outgoingPayloadKB);
    useAIContext.getState().setInput("incomingPayloadKB", incomingPayloadKB);
    useAIContext.getState().setInput("scopeCategory", scopeCategory);
    useAIContext.getState().setInput("exchangeCounterpart", exchangeCounterpart);
    useAIContext.getState().setInput("integrityScore", integrityScore);
    useAIContext.getState().setInput("reconciliationDelayDays", reconciliationDelayDays);
  }, [outgoingPayloadKB, incomingPayloadKB, scopeCategory, exchangeCounterpart, integrityScore, reconciliationDelayDays]);

  const riskPropagation = useScopeExchangeStore((s) => s.getRiskPropagation());
  const lossOfIntegrity = useScopeExchangeStore((s) => s.getLossOfIntegrity());
  const exchangeHealth = useScopeExchangeStore((s) => s.getExchangeHealth());

  // Loading and error states
  const [error, setError] = useState<Error | null>(null);

  const handleExport = () => {
    setError(null);
    try {
      exportScopeExchange();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("scopeExchange.exportFailed") || "Failed to export scope exchange");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6 font-orbitron bg-black text-neutral-100 p-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("scopeExchange.title") || "Scope Exchange"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.outgoingPayloadKB") || "Outgoing Payload (KB)"}
          </label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={outgoingPayloadKB}
            onChange={(e) => setOutgoingPayloadKB(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.incomingPayloadKB") || "Incoming Payload (KB)"}
          </label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={incomingPayloadKB}
            onChange={(e) => setIncomingPayloadKB(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.scopeCategory") || "Scope Category"}
          </label>
          <select
            value={scopeCategory}
            onChange={(e) => setScopeCategory(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="scope1">Scope 1</option>
            <option value="scope2">Scope 2</option>
            <option value="scope3">Scope 3</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.exchangeCounterpart") || "Exchange Counterpart"}
          </label>
          <input
            type="text"
            value={exchangeCounterpart}
            onChange={(e) => setExchangeCounterpart(e.target.value)}
            placeholder="e.g., DE-PARTNER-001"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.integrityScore") || "Integrity Score (0-100)"}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={integrityScore}
            onChange={(e) => setIntegrityScore(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.reconciliationDelayDays") || "Reconciliation Delay (Days)"}
          </label>
          <input
            type="number"
            min="0"
            value={reconciliationDelayDays}
            onChange={(e) => setReconciliationDelayDays(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("scopeExchange.downstreamPropCoeff") || "Downstream Propagation Coefficient"}
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={downstreamPropCoeff}
            onChange={(e) => setDownstreamPropCoeff(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Derived Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("scopeExchange.riskPropagation") || "Risk Propagation"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {riskPropagation.toFixed(2)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("scopeExchange.lossOfIntegrity") || "Loss of Integrity"}
          </div>
          <div className="text-lg font-orbitron text-orange-400">
            {lossOfIntegrity.toFixed(3)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("scopeExchange.exchangeHealth") || "Exchange Health"}
          </div>
          <div className={`text-lg font-orbitron ${
            exchangeHealth >= 80 ? "text-green-400" :
            exchangeHealth >= 50 ? "text-yellow-400" :
            "text-red-400"
          }`}>
            {exchangeHealth.toFixed(1)}
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("scopeExchange.export") || "Export Exchange JSON"}
        >
          {t("scopeExchange.export") || "Export Exchange JSON"}
        </button>
      </div>
    </div>
  );
}




